import { applyAction, applyActions, llmCallsToday, type Action, type GameEvent } from "./engine.js";
import { migrateState } from "./migrate.js";
import {
  parseClientMessage,
  type ClientMessage,
  type DeviceInfo,
  type ExportBundle,
  type Outbound,
  type ReplyPayload,
} from "./protocol.js";
import { createInitialState } from "./engine.js";
import { isExportBundle, type Store } from "./store.js";
import { HOUR, MINUTE, dayKey, type Clock } from "./time.js";
import type { GameState } from "./types.js";

export interface BrainInput {
  state: GameState;
  text: string;
  now: number;
}

export interface BrainOutput {
  text: string;
  actions: Action[];
  usage: { inputTokens: number; outputTokens: number; calls: number };
  /** Set when the model failed; text is then a friendly fallback. */
  error?: string;
}

/** Conversation brain: LLM on the server, rule-based mock in local mode and CI. */
export interface Brain {
  respond(input: BrainInput): Promise<BrainOutput>;
}

export interface QuickResult {
  actions: Action[];
  text: string;
  endConversation: boolean;
  dismiss: boolean;
}
/** Deterministic fast path for refusals and mutes (instant, 100% reliable, no LLM cost). */
export type QuickIntent = (text: string, state: GameState, now: number) => QuickResult | null;

export const FALLBACK_LINES = {
  error: "Straciłem wątek, coś z połączeniem. Powiesz to jeszcze raz?",
  limit: "Na dziś mam już zdarte gardło. Zapisz to w notesie, jutro pogadamy dłużej.",
} as const;

const ANSWER_WINDOW = 10 * MINUTE;

/**
 * The single source of truth for one user (runs inside the Durable Object, or in the browser in local mode).
 * All mutations go through core actions, are persisted via Store, then broadcast as state + events.
 */
export class RoomCore {
  private state!: GameState;
  private rev = 0;
  private loaded = false;

  constructor(
    private readonly store: Store,
    private readonly brain: Brain,
    private readonly clock: Clock,
    private readonly quick: QuickIntent | null = null,
  ) {}

  async load(): Promise<void> {
    if (this.loaded) return;
    const stored = await this.store.load();
    const now = this.clock.now();
    this.state = stored ? migrateState(stored, now) : createInitialState(now);
    this.loaded = true;
  }

  get snapshot(): GameState {
    return this.state;
  }

  get revision(): number {
    return this.rev;
  }

  private async commit(next: GameState, events: GameEvent[], origin: string | null): Promise<Outbound[]> {
    const prev = this.state;
    if (next === prev && events.length === 0) return [];
    await this.store.save(prev, next);
    this.state = next;
    this.rev++;
    const out: Outbound[] = [{ to: "all", msg: { t: "state", rev: this.rev, state: next } }];
    if (events.length) out.push({ to: "all", msg: { t: "events", rev: this.rev, events, origin } });
    return out;
  }

  private apply(action: Action): { next: GameState; events: GameEvent[]; ok: boolean; result: string } {
    const r = applyAction(this.state, action, this.clock.now());
    return { next: r.state, events: r.events, ok: r.ok, result: r.result };
  }

  /** Handle one raw message from a device. */
  async handle(device: DeviceInfo, raw: unknown): Promise<Outbound[]> {
    await this.load();
    const msg = parseClientMessage(raw);
    if (!msg)
      return [{ to: device.id, msg: { t: "error", code: "bad_message", message: "Nieznana wiadomość." } }];
    return this.dispatch(device, msg);
  }

  async dispatch(device: DeviceInfo, msg: ClientMessage): Promise<Outbound[]> {
    await this.load();
    switch (msg.t) {
      case "ping":
        return [{ to: device.id, msg: { t: "pong" } }];
      case "hello":
        return this.hello(msg.device, msg.tz, msg.visible);
      case "visible": {
        const r = this.apply({ type: "device_seen", ...device, visible: msg.visible });
        return this.commit(r.next, [], device.id);
      }
      case "call_here": {
        const r = this.apply({
          type: "set_location",
          deviceId: device.id,
          deviceName: device.name,
          profile: device.profile,
        });
        return this.commit(r.next, r.events, device.id);
      }
      case "act": {
        const r = this.apply(msg.action);
        const out = r.ok ? await this.commit(r.next, r.events, device.id) : [];
        if (msg.id) out.push({ to: device.id, msg: { t: "ack", re: msg.id, ok: r.ok, result: r.result } });
        return out;
      }
      case "say":
        return this.say(device, msg.id, msg.text);
    }
  }

  private async hello(device: DeviceInfo, tz: string, visible: boolean): Promise<Outbound[]> {
    let s = this.state;
    const now = this.clock.now();
    s = applyAction(s, { type: "device_seen", ...device, visible }, now).state;
    // First device ever gets the companion; the user's zone is taken from the first hello.
    if (!s.location.deviceId) {
      s = applyAction(
        s,
        { type: "set_location", deviceId: device.id, deviceName: device.name, profile: device.profile },
        now,
      ).state;
      if (!s.onboarded) s = applyAction(s, { type: "update_settings", patch: { timezone: tz } }, now).state;
    }
    const out = await this.commit(s, [], device.id);
    // The greeting device always gets a full snapshot (resync after reconnect).
    if (!out.some((o) => o.msg.t === "state"))
      out.push({ to: device.id, msg: { t: "state", rev: this.rev, state: this.state } });
    return out;
  }

  private async say(device: DeviceInfo, id: string, text: string): Promise<Outbound[]> {
    const now = this.clock.now();
    const actions: Action[] = [{ type: "chat", role: "user", text }];
    let reply: Omit<ReplyPayload, "results">;
    let lp = this.state.nudge.lastPrompt;
    if (lp && now - lp.at > ANSWER_WINDOW) lp = null;

    const quick = this.quick?.(text, this.state, now) ?? null;
    if (quick) {
      actions.push(...quick.actions);
      reply = { text: quick.text, endConversation: quick.endConversation, question: false, source: "quick" };
      if (lp) actions.push({ type: "prompt_outcome", outcome: quick.dismiss ? "dismissed" : "answered" });
    } else if (llmCallsToday(this.state, now) >= this.state.settings.llmDailyLimit) {
      reply = { text: FALLBACK_LINES.limit, endConversation: true, question: false, source: "limit" };
    } else {
      // Snapshot for the model; actions apply to whatever the state is when it answers.
      const withUser = applyAction(this.state, actions[0]!, now).state;
      const out = await this.brain.respond({ state: withUser, text, now });
      actions.push(...out.actions);
      for (let i = 0; i < out.usage.calls; i++)
        actions.push({
          type: "llm_usage",
          inputTokens: i === 0 ? out.usage.inputTokens : 0,
          outputTokens: i === 0 ? out.usage.outputTokens : 0,
        });
      if (out.usage.calls && this.store.logUsage)
        await this.store
          .logUsage(dayKey(now, this.state.settings.timezone), out.usage.inputTokens, out.usage.outputTokens)
          .catch(() => {});
      reply = {
        text: out.text,
        endConversation: false,
        question: /\?\s*$/.test(out.text),
        source: out.error ? "fallback" : "llm",
      };
      if (lp) actions.push({ type: "prompt_outcome", outcome: "answered" });
    }
    actions.push({ type: "chat", role: "assistant", text: reply.text });

    const r = applyActions(this.state, actions, this.clock.now());
    const out = await this.commit(r.state, r.events, device.id);
    const results = r.results
      .filter((x) => x.action.type !== "chat" && x.action.type !== "llm_usage")
      .map((x) => ({ type: x.action.type, ok: x.ok, result: x.result }));
    out.push({ to: device.id, msg: { t: "reply", re: id, reply: { ...reply, results } } });
    return out;
  }

  /** Fire due reminders and handle "come home". Returns messages to broadcast. */
  async alarm(): Promise<Outbound[]> {
    await this.load();
    const now = this.clock.now();
    const actions: Action[] = this.state.reminders
      .filter((r) => r.status === "pending" && r.at <= now)
      .map((r) => ({ type: "reminder_fire", id: r.id }));
    const home = this.comeHomeTarget(now);
    if (home)
      actions.push({ type: "set_location", deviceId: home.id, deviceName: home.name, profile: home.profile });
    if (!actions.length) return [];
    const r = applyActions(this.state, actions, now);
    return this.commit(r.state, r.events, null);
  }

  /** Next time the alarm should run, or null. */
  nextAlarmAt(): number | null {
    if (!this.loaded) return null;
    const times = this.state.reminders.filter((r) => r.status === "pending").map((r) => r.at);
    const s = this.state;
    if (s.settings.comeHomeAfterHours > 0 && s.location.profile === "pocket") {
      const host = s.devices.find((d) => d.id === s.location.deviceId);
      if (host) times.push(Math.max(host.lastSeen, s.location.since) + s.settings.comeHomeAfterHours * HOUR);
    }
    return times.length ? Math.max(this.clock.now() + 1000, Math.min(...times)) : null;
  }

  private comeHomeTarget(now: number): DeviceInfo | null {
    const s = this.state;
    const hours = s.settings.comeHomeAfterHours;
    if (!hours || s.location.profile !== "pocket") return null;
    const host = s.devices.find((d) => d.id === s.location.deviceId);
    const lastActive = Math.max(host?.lastSeen ?? 0, s.location.since);
    if (host?.visible || now - lastActive < hours * HOUR) return null;
    const wall = s.devices.filter((d) => d.profile === "wall").sort((a, b) => b.lastSeen - a.lastSeen)[0];
    return wall ? { id: wall.id, name: wall.name, profile: wall.profile } : null;
  }

  async exportProfile(): Promise<ExportBundle> {
    await this.load();
    return this.store.exportAll(this.state);
  }

  /** Testing only (debug mode): progress back to zero, settings and known devices stay, onboarding again. */
  async resetProfile(): Promise<Outbound[]> {
    await this.load();
    const now = this.clock.now();
    const state = createInitialState(now, this.state.settings);
    state.devices = this.state.devices;
    await this.store.importAll({
      format: "squire-profile",
      version: 1,
      exportedAt: new Date(now).toISOString(),
      state,
      history: { activities: [], quests: [], reminders: [], messages: [], ratings: {} },
    });
    this.state = state;
    this.rev++;
    return [{ to: "all", msg: { t: "state", rev: this.rev, state } }];
  }

  async importProfile(bundle: unknown): Promise<Outbound[]> {
    if (!isExportBundle(bundle)) throw new Error("invalid_export");
    const now = this.clock.now();
    const state = migrateState(bundle.state, now);
    await this.store.importAll({ ...bundle, state });
    this.state = state;
    this.loaded = true;
    this.rev++;
    return [{ to: "all", msg: { t: "state", rev: this.rev, state } }];
  }
}
